import React, { memo } from 'react';
import { useTranslation } from 'react-i18next';
import _get from 'lodash/get';
import _noop from 'lodash/noop';
import { DocumentationValidationStatusEnum } from 'graphql-common';
import Button, { ButtonTypes } from '@lib/components/Button/Button';
import Select, { SelectComponents } from '@lib/components/Select/Select';
import MultiSelectValueContainer from '@lib/components/Select/components/MultiSelectValueContainer';
import IconButton, {
  IconButtonTypes,
} from '@lib/components/IconButton/IconButton';
import IconButtonWithDropDown from '@lib/components/IconButtonWithDropDown/IconButtonWithDropDown';
import DocumentationPreview from '@lib/components/DocumentationPreview/DocumentationPreview';
import { DocumentationPreviewEnum } from '@lib/components/DocumentationPreview/enums';
import SupportiveMessage, {
  SupportiveMessageColor,
  SupportiveMessageType,
} from '@lib/components/SupportiveMessage/SupportiveMessage';
import Typography from '@lib/components/Typography/Typography';
import formStyles from '@lib/components/ReactHookForm/Form.module.scss';
import { FormRef } from '@lib/components/ReactHookForm/types';
import RequiredToReadOption from '@lib/components/Select/components/RequiredToReadOption';
import RequiredToReadSingleValue from '@lib/components/Select/components/RequiredToReadSingleValue';
import DocumentsModalTableMultiselect from 'components/DocumentsModalTableMultiselect/DocumentsModalTableMultiselect';
import getDocumentActionsNavItems from 'components/FormTemplateBuilder/utils/getDocumentActionsNavItems';
import useActions, { DOCUMENT_SELECTIONS_FIELD } from './utils/useActions';
import getRequiredToReadOptions from './utils/getRequiredToReadOptions';
import styles from './QuestionDocumentField.module.scss';

const multiSelectComponents = {
  ValueContainer: MultiSelectValueContainer,
};

const requiredToReadSelectComponents = {
  Option: RequiredToReadOption,
  SingleValue: RequiredToReadSingleValue,
};

const requiredToReadOptions = getRequiredToReadOptions();

interface Props {
  parentName: string;
}

function QuestionDocumentField(props: Props) {
  const { parentName } = props;
  const { t } = useTranslation();
  const {
    errorMessage,
    isModalOpen,
    isViewLocked,
    isFullAdmin,
    currentOptions,
    selectedDocValues,
    documentsMap,
    loading,
    syntheticFormRef,
    onOpenModal,
    onCloseModal,
    onSelect,
    onRemoveDocument,
    onRequiredToReadChange,
    onUpgradePlan,
    onViewDocument,
    onVersionNotes,
    onDetails,
    renderDocumentModals,
  } = useActions({ parentName });

  if (isViewLocked && isFullAdmin) {
    return (
      <SupportiveMessage
        text={t('document-question-type-unavailable')}
        color={SupportiveMessageColor.Yellow}
        type={SupportiveMessageType.FilledWithLeftBorder}
        actions={
          <Button
            buttonText={t('upgrade-plan')}
            buttonType={ButtonTypes.primaryFilled}
            onClick={onUpgradePlan}
          />
        }
      />
    );
  }

  const renderedDocuments = currentOptions.map(
    (opt: Record<string, unknown>, index: number) => {
      const docId = _get(opt, 'optionDocumentation.documentationId') as string;
      if (!docId) return null;
      const doc = documentsMap.get(docId);
      const requiredToRead = _get(
        opt,
        'optionDocumentation.requiredToRead',
        true,
      ) as boolean;
      const requiredToReadValue = requiredToReadOptions.find(
        (o) => o.value === requiredToRead,
      );

      if (!doc) {
        if (loading) return null;
        return (
          <div key={docId} className={styles.documentItem}>
            <div className={styles.documentPreview}>
              <SupportiveMessage
                text={t('document-unavailable')}
                leftIconSymbolsOutlined
                color={SupportiveMessageColor.Gray}
                hasBottomSpace={false}
              />
            </div>
            <IconButton
              icon="close"
              onClick={() => onRemoveDocument(index)}
              rounded
              type={IconButtonTypes.SecondaryFilled}
            />
          </div>
        );
      }

      return (
        <div key={docId} className={styles.documentItem}>
          <div className={styles.documentPreview}>
            <DocumentationPreview
              attachment={doc.currentVersion?.attachmentAttached}
              view={DocumentationPreviewEnum.DocumentRowCard}
              customName={doc.name || docId}
              notes={doc.currentVersion?.notes || undefined}
              isFavorite={!!doc.favorited}
              isRequired={requiredToRead}
              notValidated={
                doc.validationStatus !==
                DocumentationValidationStatusEnum.Validated
              }
              actions={
                <IconButtonWithDropDown
                  buttonProps={{ icon: 'more_vert' }}
                  popoverProps={{
                    width: 220,
                    placement: 'bottomRight',
                  }}
                  navItems={getDocumentActionsNavItems({
                    doc,
                    onViewDocument,
                    onVersionNotes,
                    onDetails,
                    t,
                  })}
                />
              }
            />
          </div>
          <div className={styles.requiredToReadSelect}>
            <Select
              onChange={(newValue) =>
                onRequiredToReadChange(
                  index,
                  newValue as { value: boolean } | null,
                )
              }
              options={requiredToReadOptions}
              value={requiredToReadValue}
              isClearable={false}
              isSearchable={false}
              fullWidthMenu={false}
              components={requiredToReadSelectComponents}
            />
          </div>
          <IconButton
            icon="close"
            onClick={() => onRemoveDocument(index)}
            rounded
            type={IconButtonTypes.SecondaryFilled}
          />
        </div>
      );
    },
  );

  return (
    <>
      <Select
        label={t('attach-documents')}
        onChange={_noop}
        isMulti
        isAsync
        isClearable={false}
        isSearchable={false}
        openMenuOnClick={false}
        openMenuOnFocus={false}
        menuIsOpen={false}
        defaultOptions={false}
        value={selectedDocValues}
        onSelectWrapperMouseDown={onOpenModal}
        components={multiSelectComponents as unknown as SelectComponents}
        hasError={!!errorMessage}
      />
      {errorMessage && (
        <Typography variant="label" className={formStyles.formItemError} block>
          {errorMessage}
        </Typography>
      )}
      {renderedDocuments.some(Boolean) && (
        <div className={styles.documentsList}>{renderedDocuments}</div>
      )}
      <DocumentsModalTableMultiselect
        isOpen={isModalOpen}
        onClose={onCloseModal}
        onSelect={onSelect}
        formRef={syntheticFormRef as React.RefObject<FormRef | undefined>}
        fieldName={DOCUMENT_SELECTIONS_FIELD}
        selectionLimit={5}
      />
      {renderDocumentModals}
    </>
  );
}

export default memo(QuestionDocumentField);
